'use client';

import { useLanguageStore, useLanguage as useCurrentLanguage, type Language } from '../stores/languageStore';
import { useApiClient } from '../contexts/ApiContext';
import { useRuntime } from '../runtime/RuntimeContext';
import { useAuthStore } from '../stores/authStore';
import { withLangPrefix } from '../i18n/locale';
import { UpdateLanguageLanguageCodeEnum } from '../services/generated';

interface UseLanguageReturn {
  language: Language;
  changeLanguage: (lang: Language) => void;
}

const LANGUAGE_CODE: Record<Language, UpdateLanguageLanguageCodeEnum> = {
  en: UpdateLanguageLanguageCodeEnum.En,
  ru: UpdateLanguageLanguageCodeEnum.Ru,
  uz: UpdateLanguageLanguageCodeEnum.Uz,
  fa: UpdateLanguageLanguageCodeEnum.Fa,
  pt: UpdateLanguageLanguageCodeEnum.Pt,
  tr: UpdateLanguageLanguageCodeEnum.Tr,
};

export const useLanguage = (): UseLanguageReturn => {
  const language = useCurrentLanguage();
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const apiClient = useApiClient();
  const runtime = useRuntime();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);

    const synced = isAuthenticated
      ? apiClient.api
        .profileSettingsControllerUpdateLanguage({
          languageCode: LANGUAGE_CODE[lang],
        })
        .catch((error: unknown) => {
          console.error('Failed to sync language with backend:', error);
        })
      : Promise.resolve();

    // WHY: язык веба живёт в адресе — layout, dir для фарси и метаданные меняются только полной загрузкой новой страницы;
    // ждём запрос в профиль, иначе уход со страницы его оборвёт
    if (runtime === 'web') {
      void synced.then(() => {
        const { pathname, search, hash } = window.location;
        window.location.assign(withLangPrefix(lang, pathname) + search + hash);
      });
    }
  };

  return {
    language,
    changeLanguage,
  };
};

export type { Language };
